// src/components/DeploymentFilesModal.js
import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import "./DeploymentFilesModal.css";

// Keep these in step with Server.js (MAX_UPLOAD_MB, MAX_PHOTOS_PER_DEPLOYMENT, IMAGE_TYPES)
const MAX_PHOTOS = 10;
const MAX_PDF_BYTES = 10 * 1024 * 1024;
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const PHOTO_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const isPdf = (file) =>
  file.type === "application/pdf" || (!file.type && /\.pdf$/i.test(file.name));

const sameFile = (a, b) =>
  a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

export default function DeploymentFilesModal({
  deploymentId,
  onClose,
  onFilesUpdated,
  existingPhotoCount = 0,
  hasAckPdf = false,
}) {
  const [ackPdfFile, setAckPdfFile] = useState(null);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const pdfInputRef = useRef(null);
  const photoInputRef = useRef(null);

  const photoSlotsLeft = Math.max(0, MAX_PHOTOS - existingPhotoCount - photoFiles.length);

  // Handle PDF file selection
  const handlePdfChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow choosing the same file again later
    if (!file) return;

    if (!isPdf(file)) {
      setError("Please select a valid PDF file.");
      return;
    }
    if (file.size > MAX_PDF_BYTES) {
      setError("PDF file size must be less than 10MB.");
      return;
    }

    setAckPdfFile(file);
    setError("");
  };

  // Handle photo files selection
  const handlePhotosChange = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting a photo after removing it
    if (!picked.length) return;

    const problems = [];
    const accepted = [];
    let slots = photoSlotsLeft;

    picked.forEach((file) => {
      if (!PHOTO_TYPES.includes(file.type)) {
        problems.push(`${file.name} is not a JPG, PNG or WEBP image.`);
      } else if (file.size > MAX_PHOTO_BYTES) {
        problems.push(`${file.name} is too large. Maximum size is 5MB per photo.`);
      } else if (photoFiles.some((f) => sameFile(f, file)) || accepted.some((f) => sameFile(f, file))) {
        // already in the list; skip quietly
      } else if (slots <= 0) {
        problems.push(`${file.name} was not added: a deployment can have at most ${MAX_PHOTOS} photos.`);
      } else {
        accepted.push(file);
        slots -= 1;
      }
    });

    if (accepted.length) setPhotoFiles((prev) => [...prev, ...accepted]);
    setError(problems.join(" "));
  };

  // Remove a photo from the list
  const removePhoto = (index) => {
    setPhotoFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  // Remove the PDF
  const removePdf = () => {
    setAckPdfFile(null);
    if (pdfInputRef.current) pdfInputRef.current.value = "";
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!ackPdfFile && photoFiles.length === 0) {
      setError("Please select at least one file to upload.");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      setError("You appear to be logged out. Please log in again.");
      return;
    }

    setUploading(true);
    setError("");
    setSuccess("");

    try {
      // One request with all files: field names must be "ack_pdf" and "photos"
      const formData = new FormData();
      if (ackPdfFile) formData.append("ack_pdf", ackPdfFile);
      photoFiles.forEach((photo) => formData.append("photos", photo));

      const response = await fetch(`/api/deployments/${deploymentId}/files`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }, // do NOT set Content-Type for FormData
        body: formData,
      });

      const contentType = response.headers.get("content-type") || "";
      const result = contentType.includes("application/json")
        ? await response.json().catch(() => ({}))
        : {};

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error("Your session has expired. Please log in again.");
        }
        throw new Error(result.error || `Failed to upload files (${response.status})`);
      }

      setSuccess(
        result.replaced_ack_pdf
          ? "Files uploaded. The previous acknowledgement PDF was replaced."
          : "Files uploaded successfully!"
      );

      // Clear form
      setAckPdfFile(null);
      setPhotoFiles([]);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
      if (photoInputRef.current) photoInputRef.current.value = "";

      // Notify parent component
      if (onFilesUpdated) onFilesUpdated(result.files || []);
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.message || "Failed to upload files. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  return createPortal(
    <div className="forced-upload-overlay">
      <div className="upload-modal-content-box">
        <div className="files-modal">
          <div className="files-modal-header">
            <h3>Upload Deployment Files</h3>
            <button className="close-btn" onClick={onClose} disabled={uploading}>&times;</button>
          </div>

          <form onSubmit={handleSubmit} className="files-modal-body">
            {error && <div className="alert alert-error">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}

            {/* PDF Upload Section */}
            <div className="upload-section">
              <label className="upload-label">
                <span className="label-text">Endorsed Movement Acknowledgement (PDF)</span>
                <span className="label-required">*Required</span>
              </label>

              {hasAckPdf && (
                <p className="upload-hint">
                  A PDF is already uploaded. Choosing a new one will replace it.
                </p>
              )}

              <div className="file-input-wrapper">
                <input
                  ref={pdfInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handlePdfChange}
                  className="file-input"
                  id="pdf-input"
                  disabled={uploading}
                />
                <label htmlFor="pdf-input" className="file-input-label">
                  📄 Choose PDF File
                </label>
              </div>

              {ackPdfFile && (
                <div className="selected-file">
                  <div className="file-info">
                    <span className="file-icon">📄</span>
                    <div className="file-details">
                      <span className="file-name">{ackPdfFile.name}</span>
                      <span className="file-size">{formatFileSize(ackPdfFile.size)}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removePdf}
                    className="remove-file-btn"
                    title="Remove file"
                    disabled={uploading}
                  >
                    &times;
                  </button>
                </div>
              )}
            </div>

            {/* Photos Upload Section */}
            <div className="upload-section">
              <label className="upload-label">
                <span className="label-text">Deployment Photos</span>
                <span className="label-optional">Optional</span>
              </label>

              <p className="upload-hint">
                {existingPhotoCount + photoFiles.length} of {MAX_PHOTOS} photos used
                {photoSlotsLeft === 0 ? " (limit reached: delete a photo to add more)" : ""}. JPG, PNG or
                WEBP, up to 5MB each.
              </p>

              <div className="file-input-wrapper">
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={handlePhotosChange}
                  className="file-input"
                  id="photo-input"
                  disabled={uploading || photoSlotsLeft === 0}
                />
                <label
                  htmlFor="photo-input"
                  className={`file-input-label${photoSlotsLeft === 0 ? " file-input-label-disabled" : ""}`}
                >
                  📷 Choose Photos
                </label>
              </div>

              {photoFiles.length > 0 && (
                <div className="selected-files-list">
                  {photoFiles.map((file, index) => (
                    <div key={`${file.name}-${file.size}-${file.lastModified}`} className="selected-file">
                      <div className="file-info">
                        <span className="file-icon">🖼️</span>
                        <div className="file-details">
                          <span className="file-name">{file.name}</span>
                          <span className="file-size">{formatFileSize(file.size)}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="remove-file-btn"
                        title="Remove file"
                        disabled={uploading}
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="files-modal-footer">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={uploading || (!ackPdfFile && photoFiles.length === 0)}
              >
                {uploading ? "Uploading..." : "Upload Files"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}
